import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from '../../../../../hooks';
import proofkitFilemakerService from '../../../../../services/proofkitFilemakerService';
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui';

const CONTACT_SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACT_SEGMENTS || "contact_segments";
const CONTACTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACTS || "contacts";
const SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_SEGMENTS || "segments";

export default function ContactSegmentManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState(null);
  const [segments, setSegments] = useState([]);
  const [contactSegments, setContactSegments] = useState([]);
  const [availableSegments, setAvailableSegments] = useState([]);
  const [contactRecordId, setContactRecordId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch contact details prioritizing ClavePrincipal
      let contactRecord = null;
      try {
        const findRes = await proofkitFilemakerService.findRecords(CONTACTS_LAYOUT, {
          query: [{ ClavePrincipal: id }],
          limit: 1,
        });
        contactRecord = Array.isArray(findRes?.data) ? findRes.data[0] : null;
      } catch (_) {
        contactRecord = null;
      }
      if (!contactRecord) {
        const getRes = await proofkitFilemakerService.getRecord(CONTACTS_LAYOUT, id);
        contactRecord = getRes?.data || null;
      }
      const contactData = contactRecord?.fieldData || {};
      const contactKey = contactData?.ClavePrincipal || String(contactRecord?.recordId || id);
      const contactRid = contactRecord?.recordId || null;

      setContact({
        id: contactKey,
        nombre: contactData.nombre || "",
        email: contactData.email || "",
      });
      setContactRecordId(contactRid);

      // Fetch all segments
      const segmentsRes = await proofkitFilemakerService.getAllRecords(SEGMENTS_LAYOUT);
      const allSegments = (segmentsRes?.data || []).map(row => {
        const fd = row?.fieldData || {};
        const segKey = fd?.ClavePrincipal || "";
        return {
          id: segKey || row?.recordId,
          key: segKey,
          nombre: fd?.nombre || "",
          descripcion: fd?.descripcion || "",
        };
      });
      setSegments(allSegments);

      // Fetch contact segments
      const contactSegmentsRes = await proofkitFilemakerService.findRecords(CONTACT_SEGMENTS_LAYOUT, {
        query: [{ contact_id: contactKey }],
        limit: 100,
      });
      const contactSegs = (contactSegmentsRes?.data || []).map(record => ({
        id: record.recordId,
        segment_id: record.fieldData?.segment_id || "",
      }));
      setContactSegments(contactSegs);

      // Calculate available segments
      const contactSegIds = contactSegs.map(cs => String(cs.segment_id));
      const available = allSegments.filter(seg => !contactSegIds.includes(seg.key || String(seg.id)));
      setAvailableSegments(available);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addToSegment = async (segmentId) => {
    try {
      // Resolver ClavePrincipal de contacto
      let contactKey = contact?.id || id;
      if (!contactKey || contactKey === id) {
        const fr = await proofkitFilemakerService.findRecords(CONTACTS_LAYOUT, { query: [{ ClavePrincipal: id }], limit: 1 });
        const fd = fr?.data?.[0]?.fieldData || {};
        contactKey = fd?.ClavePrincipal || contactKey;
      }

      // Resolver ClavePrincipal de segmento
      let segmentKey = segmentId;
      const segObj = segments.find(s => String(s.id) === String(segmentId));
      if (segObj?.key) {
        segmentKey = segObj.key;
      } else {
        const sfr = await proofkitFilemakerService.findRecords(SEGMENTS_LAYOUT, { query: [{ ClavePrincipal: segmentKey }], limit: 1 });
        const fd = sfr?.data?.[0]?.fieldData || {};
        segmentKey = fd?.ClavePrincipal || segmentKey;
      }

      await proofkitFilemakerService.createRecord(CONTACT_SEGMENTS_LAYOUT, {
        contact_id: contactKey,
        segment_id: segmentKey,
      });
      toast({
        title: "Éxito",
        description: "Contacto agregado al segmento",
      });
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo agregar al segmento",
        variant: "destructive",
      });
    }
  };

  const removeFromSegment = async (contactSegmentId) => {
    try {
      await proofkitFilemakerService.deleteRecord(CONTACT_SEGMENTS_LAYOUT, contactSegmentId);
      toast({
        title: "Éxito",
        description: "Contacto removido del segmento",
      });
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo remover del segmento",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!contact) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(`/campanas/contacts/${contactRecordId || id}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Segmentos del Contacto</h1>
            <p className="text-muted-foreground">{contact.nombre} - {contact.email}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Segmentos Actuales</CardTitle>
          </CardHeader>
          <CardContent>
            {contactSegments.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No hay segmentos asignados</p>
            ) : (
              <div className="space-y-2">
                {contactSegments.map((cs) => {
                  const segment = segments.find(s => s.id === cs.segment_id);
                  return (
                    <div key={cs.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{segment?.nombre || "Segmento desconocido"}</p>
                        <p className="text-sm text-muted-foreground">{segment?.descripcion}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromSegment(cs.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Segmentos Disponibles</CardTitle>
          </CardHeader>
          <CardContent>
            {availableSegments.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No hay segmentos disponibles</p>
            ) : (
              <div className="space-y-2">
                {availableSegments.map((segment) => (
                  <div key={segment.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{segment.nombre}</p>
                      <p className="text-sm text-muted-foreground">{segment.descripcion}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addToSegment(segment.id)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
